import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Form } from 'react-bootstrap';

// Custom Hook 1: useLocalStorage
const useLocalStorage = (key, initialValue) => {
  console.log('🔧 Custom Hook: useLocalStorage initialized for key:', key);

  const [value, setValue] = useState(() => {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : initialValue;
  });

  useEffect(() => {
    console.log('💾 Saving to localStorage:', key, value);
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};

// Custom Hook 2: useToggle
const useToggle = (initialValue = false) => {
  const [value, setValue] = useState(initialValue);

  const toggle = () => {
    console.log('🔀 Toggle: Changing from', value, 'to', !value);
    setValue(prev => !prev);
  };

  return [value, toggle];
};

// Custom Hook 3: useDebounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    console.log('⏱️ Debounce: Setting timer for', delay, 'ms');
    const timer = setTimeout(() => {
      console.log('⏱️ Debounce: Timer expired, updating value to', value);
      setDebouncedValue(value);
    }, delay);

    return () => {
      console.log('⏱️ Debounce: Clearing timer');
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Custom Hook 4: useFetch (simplified)
const useFetch = (url) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('🌐 useFetch: Fetching from', url);
    setLoading(true);

    fetch(url)
      .then(res => res.json())
      .then(data => {
        console.log('✅ useFetch: Data received', data);
        setData(data);
        setLoading(false);
      })
      .catch(err => {
        console.log('❌ useFetch: Error', err.message);
        setError(err);
        setLoading(false);
      });
  }, [url]);

  return { data, loading, error };
};

const Lesson12_CustomHooks = () => {
  const [name, setName] = useLocalStorage('userName', '');
  const [isVisible, toggleVisible] = useToggle(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 1000);

  useEffect(() => {
    console.log('📖 Lesson 12: Custom Hooks - Loaded');
    console.log('🔧 Custom hooks let you reuse stateful logic');
  }, []);

  useEffect(() => {
    if (debouncedSearch) {
      console.log('🔍 Search query after debounce:', debouncedSearch);
    }
  }, [debouncedSearch]);

  return (
    <LessonLayout 
      lessonNumber={12}
      title="Custom Hooks" 
      description="Create reusable custom hooks for common patterns"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>Creating custom hooks</li>
          <li>Reusing stateful logic across components</li>
          <li>Common custom hook patterns</li>
          <li>Hook composition</li>
        </ul>
      </Alert>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>useLocalStorage Hook</Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Label>Name (saved to localStorage)</Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                />
              </Form.Group>
              <Alert variant="info" className="mt-3 mb-0">
                Value persists even after page refresh!
              </Alert>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>useToggle Hook</Card.Header>
            <Card.Body>
              <Button onClick={toggleVisible} variant="primary">
                Toggle Content
              </Button>
              {isVisible && (
                <Alert variant="success" className="mt-3 mb-0">
                  ✅ Content is visible!
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>useDebounce Hook</Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Label>Search (debounced by 1s)</Form.Label>
                <Form.Control
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                />
              </Form.Group>
              <Alert variant="secondary" className="mt-3 mb-0">
                <div>Current Input: {searchTerm}</div>
                <div>Debounced Value: {debouncedSearch}</div>
              </Alert>
            </ Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Custom Hooks Pattern</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`// Custom Hook
function useCounter(initialValue = 0) {
  const [count, setCount] = useState(initialValue);
  
  const increment = () => setCount(c => c + 1);
  const decrement = () => setCount(c => c - 1);
  const reset = () => setCount(initialValue);
  
  return { count, increment, decrement, reset };
}

// Usage
const { count, increment } = useCounter(0);`}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Custom Hooks Rules:</strong>
        <ul className="mb-0">
          <li>Custom hook names must start with "use" (e.g., useCustomHook)</li>
          <li>Can call other hooks inside custom hooks</li>
          <li>Share logic, not state (each component gets its own state)</li>
          <li>Great for extracting component logic into reusable functions</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson12_CustomHooks;
